import React, { useState } from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { updateRoute, getWizardRoute, getSensorWizardRoute } from '../route'
import { SelectSensorOption } from './select-sensor-option'
import { useSplitClient } from 'contrastBase/state'
import {
  IconMerge1,
  IconApplication,
  IconCloud,
  IconScan
} from '@contrast-security-inc/design-system-foundations'
import { useOnboardingService } from './use-onboarding-service'
import { useTeamserverUrl } from '../teamserver-url/api/use-teamserver-url'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { useSirOnboardingSettings } from '../../api/queries/platform-service/platform-service'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Text } from '@contrast-security-inc/design-system-typography'
import { SCMType } from '../../api/queries/onboarding-service/onboarding-service'

export interface SelectSensorTypeProps {
  organizationId: string
  isHarmonyEnabled: boolean
  isProjectReposEnabled: boolean
  isServerlessEnabled: boolean
  isAgentEnabled: boolean
}

const titleCss = css`
  margin: 0px;
  padding-top: 32px;
`

export const SelectSensorType = ({
  organizationId,
  isHarmonyEnabled,
  isProjectReposEnabled,
  isServerlessEnabled,
  isAgentEnabled
}: SelectSensorTypeProps) => {
  const { formatMessage } = useIntl()
  const { isSplitTreatmentOn } = useSplitClient()

  // Remove once the feature flags are removed
  const gitlabEnabled = isSplitTreatmentOn('PROD-443_sir_gitlab')
  const bitbucketEnabled = isSplitTreatmentOn('PROD-443_sir_bitbucket')

  const onSelected = (selected: string) => {
    let routePath = getSensorWizardRoute(organizationId)

    if (selected === 'add-agent') {
      routePath = getWizardRoute(organizationId)
    } else if (selected === 'add-serverless-account') {
      routePath = `#/${organizationId}/serverless/connect_account`
    } else if (selected === 'add-repositories') {
      if (!gitlabEnabled && !bitbucketEnabled) {
        // remove this once the feature flags are removed
        return setOnboardingServiceEnabled(true)
      } else {
        routePath = `#/${organizationId}/scm-options`
      }
    }

    updateRoute(routePath)
  }
  const { teamserverUrl } = useTeamserverUrl()
  const [onboardingServiceEnabled, setOnboardingServiceEnabled] =
    useState(false)

  const { data } = useSirOnboardingSettings(
    organizationId,
    onboardingServiceEnabled
  )
  const {
    hostName,
    port: portData,
    protocol: protocolData
  } = data?.globalOnboardingEndpoint ?? {}

  const protocol = protocolData ? protocolData + '://' : 'https://'
  const port = portData ? ':' + portData : ''
  const destinationUrl = hostName ? `${protocol}${hostName}${port}` : ''

  useOnboardingService(
    organizationId,
    teamserverUrl?.replace('/Contrast', ''),
    destinationUrl,
    SCMType.GITHUB,
    {
      enabled: onboardingServiceEnabled && !!organizationId && !!destinationUrl,
      onSuccess: (data) => {
        if (data.scmInstallationUrl) {
          window.open(data.scmInstallationUrl, '_blank')
        } else {
          setOnboardingServiceEnabled(false)
          toast(
            formatMessage({ id: 'error_addSensorWizard_adding_repository' }),
            {
              variant: 'error'
            }
          )
        }
      },
      onError: () => {
        setOnboardingServiceEnabled(false)
        toast(
          formatMessage({ id: 'error_addSensorWizard_adding_repository' }),
          {
            variant: 'error'
          }
        )
      }
    }
  )

  return (
    <>
      <Stack
        spacing="32px"
        flexDirection="column"
        alignItems="center"
        data-testid="onboarding-select-sensor-type-page"
      >
        <Text as="h3" font="titleH1" className={titleCss}>
          {formatMessage({ id: 'detail_addSensorWizard_title' })}
        </Text>

        <Stack spacing="24px" alignItems="stretch">
          {isHarmonyEnabled && (
            <SelectSensorOption
              title={formatMessage({
                id: 'detail_addSensorWizard_code_title'
              })}
              type="SAST"
              description={formatMessage({
                id: 'detail_addSensorWizard_code_description'
              })}
              icon={<IconScan size="lg" color="turquoise70" />}
              onSelect={() => onSelected('add-scan-project')}
              testId="scan"
              className={css`
                padding: 32px 40px;
              `}
            />
          )}

          {isProjectReposEnabled && (
            <SelectSensorOption
              title={formatMessage({
                id: 'detail_addSensorWizard_repositories_title'
              })}
              type="SCA / SBOM"
              description={formatMessage({
                id: 'detail_addSensorWizard_repositories_description'
              })}
              icon={<IconMerge1 size="lg" color="turquoise70" />}
              onSelect={() => onSelected('add-repositories')}
              testId="repositories"
            />
          )}
          {isServerlessEnabled && (
            <SelectSensorOption
              title={formatMessage({
                id: 'detail_addSensorWizard_serverless_title'
              })}
              type="SAST / SERVERLESS"
              description={formatMessage({
                id: 'detail_addSensorWizard_serverless_description'
              })}
              icon={<IconCloud size="lg" color="turquoise70" />}
              onSelect={() => onSelected('add-serverless-account')}
              testId="serverless"
            />
          )}
          {isAgentEnabled && (
            <SelectSensorOption
              title={formatMessage({
                id: 'detail_addSensorWizard_liveApplication_title'
              })}
              type="IAST / SCA / RASP"
              description={formatMessage({
                id: 'detail_addSensorWizard_liveApplication_description'
              })}
              icon={<IconApplication size="lg" color="turquoise70" />}
              onSelect={() => onSelected('add-agent')}
              testId="agent"
            />
          )}
        </Stack>
      </Stack>
    </>
  )
}
